/**
 * Flux d'activité des contacts (chantier C10, SP2, spec § 3).
 *
 * Lecture paginée des notifications de types sociaux, CUMULATIVE : la page 1
 * remplace, les suivantes s'ajoutent (dédoublonnées par _id). Séparé du slice
 * `notifications` de la cloche, dont `fetchNotifications.fulfilled` remplace la
 * liste à chaque appel. Marquer lu met à jour l'élément ici et rafraîchit le
 * compteur de la cloche (`fetchUnreadCount`).
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { notificationService, setNotificationAuthToken } from './notificationsAPI';
import { fetchUnreadCount } from './notificationsSlice';
import { SOCIAL_NOTIFICATION_TYPES, ACTIVITY_PAGE_SIZE } from './socialTypes';
import { getTokenRedirect } from '../../utils/authRedirect';
import { tokenRequest } from '../../authConfig';
import { LOADING_STATES } from '../../constants';
import type { LoadingState } from '../../types/redux';
import type { Notification } from '../../types/sharedThesaurus';

export interface ActivityFeedState {
    items: Notification[];
    page: number;
    hasNext: boolean;
    status: LoadingState;
    error: string | null;
    /** requestId de la requête `fetchActivityPage` la plus récente ; sert à ignorer une réponse périmée (remount de ContactsPage pendant un chargement en cours). */
    requestId: string | null;
}

export const initialActivityFeedState: ActivityFeedState = {
    items: [], page: 0, hasNext: false, status: LOADING_STATES.IDLE, error: null, requestId: null,
};

const EMPTY: Notification[] = [];

async function tokenOf(instance: any): Promise<string> {
    const accessToken = await getTokenRedirect(tokenRequest, instance);
    if (!accessToken) throw new Error('Impossible d\'obtenir le token d\'accès');
    setNotificationAuthToken(accessToken);
    return accessToken;
}

export const fetchActivityPage = createAsyncThunk<
    { items: Notification[]; page: number; hasNext: boolean },
    { instance: any; page: number },
    { rejectValue: string }
>('activityFeed/fetchPage', async ({ instance, page }, { rejectWithValue }) => {
    try {
        await tokenOf(instance);
        const result = await notificationService.getAll({
            page, limit: ACTIVITY_PAGE_SIZE, type: SOCIAL_NOTIFICATION_TYPES.join(','),
        });
        return { items: result.data, page, hasNext: result.pagination.hasNext };
    } catch (error: any) {
        return rejectWithValue(error?.response?.data?.message || error?.message || 'Impossible de charger l\'activité');
    }
});

export const markActivityRead = createAsyncThunk<string, { instance: any; notificationId: string }, { rejectValue: string }>(
    'activityFeed/markRead',
    async ({ instance, notificationId }, { dispatch, rejectWithValue }) => {
        try {
            await tokenOf(instance);
            await notificationService.markAsRead(notificationId);
            void dispatch(fetchUnreadCount(instance));
            return notificationId;
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error?.message || 'Impossible de marquer comme lu');
        }
    },
);

const activityFeedSlice = createSlice({
    name: 'activityFeed',
    initialState: initialActivityFeedState,
    reducers: {
        resetActivityFeed() { return initialActivityFeedState; },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchActivityPage.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = LOADING_STATES.LOADING;
                state.error = null;
            })
            .addCase(fetchActivityPage.fulfilled, (state, action) => {
                // Une réponse d'une requête périmée (ContactsPage démonté puis remonté pendant
                // le chargement d'une page précédente) ne doit rien modifier.
                if (action.meta.requestId !== state.requestId) return;
                const { items, page, hasNext } = action.payload;
                if (page <= 1) {
                    state.items = items;
                } else {
                    const seen = new Set(state.items.map(i => i._id));
                    state.items.push(...items.filter(i => !seen.has(i._id)));
                }
                state.page = page;
                state.hasNext = hasNext;
                state.status = LOADING_STATES.SUCCEEDED;
            })
            .addCase(fetchActivityPage.rejected, (state, action) => {
                if (action.meta.requestId !== state.requestId) return;
                state.status = LOADING_STATES.FAILED;
                state.error = action.payload || action.error?.message || 'Impossible de charger l\'activité';
            })
            .addCase(markActivityRead.fulfilled, (state, action) => {
                const item = state.items.find(i => i._id === action.payload);
                if (item) item.isRead = true;
            });
    },
});

export const { resetActivityFeed } = activityFeedSlice.actions;

const sliceOf = (state: any): ActivityFeedState | undefined => state?.activityFeed;
export const selectActivityItems = (state: any): Notification[] => sliceOf(state)?.items ?? EMPTY;
export const selectActivityStatus = (state: any): LoadingState => sliceOf(state)?.status ?? LOADING_STATES.IDLE;
export const selectActivityError = (state: any): string | null => sliceOf(state)?.error ?? null;
export const selectActivityHasNext = (state: any): boolean => sliceOf(state)?.hasNext ?? false;
export const selectActivityPage = (state: any): number => sliceOf(state)?.page ?? 0;

export default activityFeedSlice.reducer;
